/**
 * GeneratingImageCard - Displays an image being generated with status
 */

import React from "react";

import toast from "react-hot-toast";
import { Download, Copy, X, CheckCircle2, AlertCircle } from "lucide-react";

import { WaveDots } from "@/components/ui/wave-loader";
import { cn } from "@/lib/utils";
import { GeneratingImage } from "@/types/image-generate-panel.types";

interface GeneratingImageCardProps {
  image: GeneratingImage;
  onRemove: (requestId: string) => void;
}

export function GeneratingImageCard({
  image,
  onRemove,
}: GeneratingImageCardProps): React.ReactElement {
  const isActive = image.status === "queued" || image.status === "processing";
  const isCompleted = image.status === "completed";
  const isFailed = image.status === "failed";

  return (
    <div
      className={cn(
        "rounded-md border text-xs flex items-center gap-2 p-1.5",
        "transition-all duration-200",
        isCompleted && "bg-green-500/5 border-green-500/20",
        isFailed && "bg-destructive/5 border-destructive/20",
        isActive && "bg-muted/30 border-border/50"
      )}
    >
      {/* Thumbnail or status icon */}
      {isCompleted && image.downloadUrl ? (
        <div className="relative h-9 w-9 flex-shrink-0 rounded overflow-hidden group">
          <img
            src={image.downloadUrl}
            alt={image.filename}
            className="h-full w-full object-cover"
            loading="lazy"
          />
          <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-0.5">
            <button
              className="p-0.5 rounded hover:bg-white/20 text-white"
              onClick={async () => {
                try {
                  await navigator.clipboard.writeText(image.downloadUrl!);
                  toast.success("URL copied");
                } catch {
                  toast.error("Failed to copy");
                }
              }}
            >
              <Copy className="w-3 h-3" />
            </button>
            <button
              className="p-0.5 rounded hover:bg-white/20 text-white"
              onClick={() => window.open(image.downloadUrl, "_blank")}
            >
              <Download className="w-3 h-3" />
            </button>
          </div>
        </div>
      ) : isActive ? (
        <div className="h-9 w-9 flex-shrink-0 rounded bg-muted/30 flex items-center justify-center">
          <WaveDots dots={3} dotSize={4} />
        </div>
      ) : (
        <div className="h-9 w-9 flex-shrink-0 rounded bg-destructive/10 flex items-center justify-center">
          <AlertCircle className="w-3.5 h-3.5 text-destructive/50" />
        </div>
      )}

      {/* Info */}
      <div className="flex-1 min-w-0">
        <p className="text-[11px] font-medium truncate">{image.filename}</p>
        <p className="text-[10px] text-muted-foreground truncate">
          {isActive && (image.status === "queued" ? "Queued" : "Generating...")}
          {isCompleted && "Done"}
          {isFailed && (image.errorMessage || "Failed")}
        </p>
      </div>

      {/* Status + remove */}
      <div className="flex items-center gap-1 flex-shrink-0">
        {isCompleted && <CheckCircle2 className="w-3.5 h-3.5 text-green-500" />}
        <button
          onClick={() => onRemove(image.requestId)}
          className="p-0.5 rounded hover:bg-muted/50 transition-colors"
        >
          <X className="w-3 h-3 text-muted-foreground/60" />
        </button>
      </div>
    </div>
  );
}
